import { useState } from 'react';
import { Search } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Avatar } from '@/components/ui/Avatar';
import { Input } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { ErrorState } from '@/components/common/ErrorState';
import {
  useConversations,
  useConversationMessages,
  useSendMessage,
  type ChatConversation,
} from '@/data/api';
import { formatRelative } from '@/lib/format';
import { cn } from '@/lib/cn';
import { ChatComposer, MessageBubble } from './Chat';
import type { Message } from '@/types/request';

export function MessagesPage() {
  const [search, setSearch] = useState('');
  const [activeId, setActiveId] = useState<string | null>(null);

  const conversationsQuery = useConversations(search);
  const conversations = conversationsQuery.data?.content ?? [];

  // Auto-select the first conversation when the list loads.
  const effectiveActiveId =
    activeId ?? (conversations.length > 0 ? conversations[0]!.id : null);

  const messagesQuery = useConversationMessages(effectiveActiveId);
  const sendMutation = useSendMessage();

  const active = conversations.find((c) => c.id === effectiveActiveId) ?? null;

  return (
    <Card padding="none" className="overflow-hidden">
      <div className="grid grid-cols-[320px_1fr] h-[calc(100vh-140px)]">
        {/* Left rail */}
        <aside className="border-r border-line flex flex-col">
          <div className="p-3 border-b border-line">
            <Input
              placeholder="Search conversations"
              iconLeft={<Search className="h-4 w-4" />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {conversationsQuery.isLoading ? (
            <div className="p-3 space-y-2 flex-1">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-16" />
              ))}
            </div>
          ) : conversationsQuery.isError ? (
            <ErrorState
              title="Couldn't load conversations"
              onRetry={() => conversationsQuery.refetch()}
            />
          ) : conversations.length === 0 ? (
            <EmptyState
              title="No conversations yet"
              description="Conversations will appear here when customers reach out, or when staff start one from a customer's profile."
            />
          ) : (
            <ul className="flex-1 overflow-y-auto scrollbar-thin divide-y divide-line-soft">
              {conversations.map((c) => (
                <ConversationRow
                  key={c.id}
                  conv={c}
                  selected={c.id === effectiveActiveId}
                  onClick={() => setActiveId(c.id)}
                />
              ))}
            </ul>
          )}
        </aside>

        {/* Right pane — open conversation */}
        {active ? (
          <section className="flex flex-col min-w-0">
            <header className="flex items-center gap-3 px-6 py-3 border-b border-line">
              <Avatar name={active.customerName ?? '?'} />
              <div className="flex-1 min-w-0">
                <div className="text-body-strong text-ink truncate">
                  {active.customerName ?? 'Customer'}
                </div>
                <div className="text-caption text-ink-muted truncate font-mono">
                  {active.customerPhone ?? '—'}
                  {active.requestReference && ` · ${active.requestReference}`}
                </div>
              </div>
            </header>
            <div className="flex-1 overflow-y-auto p-6 space-y-3 scrollbar-thin bg-canvas">
              {messagesQuery.isLoading ? (
                <Skeleton className="h-12" />
              ) : (
                (messagesQuery.data ?? []).map((m) => (
                  <MessageBubble
                    key={m.id}
                    message={{
                      id: m.id,
                      conversationId: m.conversationId,
                      authorName: m.authorName,
                      authorKind: m.authorKind,
                      body: m.body,
                      internal: m.internal,
                      createdAt: m.createdAt,
                    } as Message}
                  />
                ))
              )}
            </div>
            <div className="border-t border-line">
              <ChatComposer
                onSend={(body, internal) => {
                  if (!effectiveActiveId) return;
                  sendMutation.mutate({
                    conversationId: effectiveActiveId,
                    body,
                    internal,
                  });
                }}
              />
            </div>
          </section>
        ) : (
          <EmptyState
            title="Pick a conversation"
            description="Select a customer from the list to open the conversation."
          />
        )}
      </div>
    </Card>
  );
}

function ConversationRow({
  conv,
  selected,
  onClick,
}: {
  conv: ChatConversation;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <li
      onClick={onClick}
      className={cn(
        'flex gap-3 px-4 py-3 cursor-pointer transition-colors',
        selected ? 'bg-status-info-bg' : 'hover:bg-canvas',
      )}
    >
      <Avatar name={conv.customerName ?? '?'} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="text-body-strong text-ink truncate">
            {conv.customerName ?? 'Customer'}
          </span>
          <span className="text-caption text-ink-muted shrink-0">
            {conv.lastMessageAt ? formatRelative(conv.lastMessageAt) : ''}
          </span>
        </div>
        {conv.requestReference && (
          <div className="text-caption text-brand-blue font-mono">
            {conv.requestReference}
          </div>
        )}
        <div className="flex items-center justify-between gap-2 mt-0.5">
          <span className="text-caption text-ink-muted truncate">
            {conv.lastMessagePreview ?? 'No messages yet'}
          </span>
          {conv.unreadForStaff > 0 && (
            <span className="shrink-0 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-pill bg-brand-blue text-white text-[10px] font-bold">
              {conv.unreadForStaff}
            </span>
          )}
        </div>
      </div>
    </li>
  );
}
